import Vue from 'vue';
import Router from 'vue-router';
import NotFound from '@/views/NotFound.vue';
import Routes from '@/util/Routes';
import { Config } from '@/util/Config';
import { store } from '@/stores';
import { getModule } from 'vuex-module-decorators';
import KeycloakStore from '@/stores/user/keycloak.store';
import { ErrorStore } from '@/stores/error.store';
import { StandardErrors } from '@/util/StandardErrors';
import ErrorKey from '@/util/ErrorKey';

Vue.use(Router);

export default new Router({
  mode: 'history',
  base: Config.APP_BASE,
  scrollBehavior(to, from, savedPosition) {
    if (to.meta.isBack && from.meta.scrollTo) {
      return {x: 0, y: from.meta.scrollTo};
    } else {
      return {x: 0, y: 0};
    }
  },
  routes: [
    {
      path: '', redirect: {name: Routes.CmsDynamicPage, params: {webrootPath: 'home'}}, name: Routes.Home,
    },
    {
      path: '/browse',
      redirect: {name: Routes.CmsDynamicPage, params: {webrootPath: 'browse'}},
      name: Routes.Categories,
    },
    {
      path: '/browse/:categoryId',
      name: Routes.Category,
      component: () => import (/* webpackChunkName: "browse" */ './views/browse/BrowseProducts.vue'),
      beforeEnter: async (to, from, next) => {
        to.meta.isBack = from.meta.origin === to.fullPath;
        next();
      },
    },
    {
      path: '/my',
      component: () => import(/* webpackChunkName: "user-profile" */ './views/account/User.vue'),
      beforeEnter: async (to, from, next) => {
        const keycloakStore = getModule(KeycloakStore, store);
        const errorStore = getModule(ErrorStore, store);
        try {
          await keycloakStore.initialize();
          if (keycloakStore.authenticated) {
            next();
          } else {
            next(false);
            await keycloakStore.login();
          }
        } catch (e) {
          errorStore.setError(StandardErrors.wrapIfUnknown(e, ErrorKey.LoginStatusCheckFailed));
          next('/');
        }
      },
      children: [
        {
          path: 'profile/view',
          name: Routes.Profile,
          component: () => import(/* webpackChunkName: "profile" */ './views/account/user/UserProfile.vue'),
          meta: {accentBackground: true},
        },
        {
          path: 'profile/orders',
          name: Routes.UserOrders,
          component: () => import(/* webpackChunkName: "profile" */ './views/account/user/UserOrders.vue'),
          meta: {accentBackground: true},
        },
        {
          path: 'profile/orders/:orderId',
          name: Routes.UserOrderDetail,
          component: () => import(/* webpackChunkName: "product-details" */ './views/account/user/UserOrderDetail.vue'),
          meta: {accentBackground: true},
          props: true,
        },
        {
          path: 'profile/edit',
          name: Routes.ProfileEdit,
          component: () => import(/* webpackChunkName: "profile" */ './views/account/user/UserEdit.vue'),
          meta: {accentBackground: true},
        },
        {
          path: 'registration/complete',
          name: Routes.Register,
          component: () => import(/* webpackChunkName: "register" */ './views/account/Register.vue'),
          meta: {isRegisterHeader: true},
          props: (route) => ({fromCart: route.query.fromCart === 'true'}),
        },
        {
          path: 'registration/confirmation',
          name: Routes.RegisterConfirmation,
          component: () => import(/* webpackChunkName: "register" */ './views/account/RegisterConfirmation.vue'),
          meta: {isRegisterHeader: true},
          props: (route) => ({fromCart: route.query.fromCart === 'true'}),
        },
      ],
    },
    {
      path: '/product/:sku/:urlName/:unitAbbreviation',
      name: Routes.ProductDetail,
      component: () => import(/* webpackChunkName: "product-details" */ './views/product/ProductDetailView.vue'),
      props: true,
      beforeEnter: async (to, from, next) => {
        to.meta.origin = from.fullPath;
        to.meta.scrollTo = from.meta.currentScroll;
        next();
      },
    },
    {
      path: '/search',
      name: Routes.ProductSearch,
      component: () => import(/* webpackChunkName: "search-result" */ './views/search/SearchResult.vue'),
      props: (route) => ({query: route.query.query, category: route.query.currentCategoryId}),
      beforeEnter: async (to, from, next) => {
        to.meta.isBack = from.meta.origin === to.fullPath;
        next();
      },
    },
    {
      path: '/page/:webrootPath(.*)',
      name: Routes.CmsDynamicPage,
      component: () => import(/* webpackChunkName: "cms" */ './views/cms/DynamicPage.vue'),
      props: true,
    },
    {
      path: '/err',
      name: Routes.NotFound,
      component: NotFound,
    },
    {
      path: '*',
      redirect: '/err',
    },
  ],
});
